import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Width of an element, kept current with a ResizeObserver. Charts are drawn at
 * real pixel width (never stretched with preserveAspectRatio) so strokes keep
 * their specified thickness.
 */
export function useMeasure<T extends HTMLElement>(fallback = 640): [RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = (): void => {
      const next = node.getBoundingClientRect().width;
      if (next > 0) setWidth(next);
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, width];
}
